// src/utils/operationsWorkspace.js
//
// Pass 49B — pure derivation layer for the Bookings & Operations workspace. Everything the
// workspace shows (season bookings, season roster, metrics, read-only state, the week grid,
// the unassigned queue, conflicts) is DERIVED here from the single realtime source, via the
// canonical season-scoping helpers. No component re-implements season filtering.

import { deriveSeasonId, isHistoricalSeason, isLiveBooking, SEASONS, seasonLabel } from './scheduleModel';
import { scopeToSeason, getMembersForSeason, seasonMetrics, seasonDateRange, nextScheduledDayInSeason } from './seasonScope';
import { CURRENT_SEASON_ID, resolveCurrentTeamState, TEAM_STATE } from './instructorCategory';
import { deriveActivityType, ACTIVITY, ACTIVITY_LABEL } from './activityModel';
import { zonedDateKey, zonedWeekStart, wallClockToInstant, LEGACY_OPS_TIMEZONE } from './opsTime';

export const WORKSPACE_VIEWS = ['requests', 'calendar', 'day', 'week', 'season', 'unassigned'];
export { SEASONS, seasonLabel };

const DAY_MS = 24 * 60 * 60 * 1000;

export function msOf(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (v?.seconds) return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

// Pass 82C.2 — canonical day bucket in the ORGANIZATION's timezone (NOT the browser timezone). Defaults to the legacy
// SAS zone so SAS callers that pass no zone are byte-identical to the former Asia/Tokyo behaviour.
export function dateKeyOf(v, timeZone = LEGACY_OPS_TIMEZONE) {
  return zonedDateKey(v, timeZone);
}

// A booking has usable operational timing? Historical low-confidence rows are excluded from
// conflict maths per the existing scheduling rules (Pass 49B §22).
export function hasReliableTiming(b) {
  if (b.hasOperationalTime === false) return false;
  return msOf(b.startAt) != null;
}

function overlaps(a, b) {
  const as = msOf(a.startAt); const bs = msOf(b.startAt);
  if (as == null || bs == null) return false;
  const ae = msOf(a.endAt) || as + 60 * 60 * 1000;
  const be = msOf(b.endAt) || bs + 60 * 60 * 1000;
  return as < be && bs < ae;
}

// §22 conflict detection across ALL timed current work — a person cannot be double-booked
// across lesson/driving/concierge/admin/training/meeting. Grouped by the canonical member id.
export function detectOperationalConflicts(bookings = []) {
  const conflictIds = new Set();
  const byInstructor = {};
  for (const b of bookings) {
    if (!b.instructorId || b.status === 'canceled') continue;
    if (!hasReliableTiming(b)) continue; // exclude historical low-confidence timing
    (byInstructor[b.instructorId] = byInstructor[b.instructorId] || []).push(b);
  }
  for (const list of Object.values(byInstructor)) {
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        if (overlaps(list[i], list[j])) { conflictIds.add(list[i].id); conflictIds.add(list[j].id); }
      }
    }
  }
  return conflictIds;
}

// §7/§26 read-only for historical seasons; current season is operational. Pass 82C.2 — "historical" is relative to
// the ORGANIZATION's current season (defaults to the platform current season for SAS-compatible callers).
export function isReadOnlySeason(seasonId, currentSeasonId = CURRENT_SEASON_ID) {
  return isHistoricalSeason(seasonId, currentSeasonId);
}

// §14 scope pending booking requests to the selected season INTENTIONALLY: a request with a
// requested date in the season is eligible for that season's queue; an undated pending enquiry
// stays a global attention item (explicit choice). Prior-season dated requests are never
// silently mixed into the current season.
export function scopeRequestsToSeason(requests = [], seasonId, { timeZone = LEGACY_OPS_TIMEZONE } = {}) {
  return requests.filter((r) => {
    const dt = r.requestedStartAt || r.startAt || r.preferredStartAt || r.preferredDate || r.requestedDate || null;
    if (!dt && !r.seasonId) return true; // undated enquiry → global attention item
    // Pass 82C.2 — infer the season in the request's/org's timezone (request zone wins → org zone → legacy SAS JST).
    return deriveSeasonId({ startAt: dt, seasonId: r.seasonId, timezone: r.timezone }, { timeZone }) === seasonId;
  });
}

// §15 the unassigned operations queue for a season: canonical bookings with no assigned
// instructor. Preserves sourceInstructorLabel as provenance only (never auto-mapped).
// Pass 59B — schedule-grid hold classifiers (a hold is scheduleEntryType:'blocked', activityType:'client_hold').
export const isGridHold = (b = {}) => b.externalSource === 'sas_schedule_grid' || b.activityType === 'client_hold';
export const isCoveredGridHold = (b = {}) => isGridHold(b) && b.reconciliationState === 'covered_by_booking';
export const isStandaloneGridHold = (b = {}) => isGridHold(b) && !isCoveredGridHold(b) && b.blocksAvailability !== false && isLiveBooking(b);

export function buildUnassignedQueue(seasonBookings = []) {
  return seasonBookings
    // Pass 58F2 — exclude retired/superseded seed placeholders (active:false /
    // lifecycleState:'retired_superseded'); they are not live unassigned demand. isLiveBooking
    // also covers the previous `status !== 'canceled'` guard.
    .filter((b) => !b.instructorId && isLiveBooking(b))
    .map((b) => ({
      id: b.id,
      booking: b,
      sourceInstructorLabel: b.sourceInstructorLabel || '',
      activityType: deriveActivityType(b),
      startAt: b.startAt || null,
      serviceDate: b.serviceDate || '',
      requesterName: b.requesterName || b.leadGuest?.fullName || '',
    }))
    .sort((a, b) => (msOf(a.startAt) || Infinity) - (msOf(b.startAt) || Infinity));
}

// §9/§25 the DEFAULT operational roster for a season. Historical seasons → that season's
// actual staff (getMembersForSeason). Current season (26/27) → confirmed-CURRENT staff ONLY
// by default (the 27 unresolved roster-review people are NOT the working schedule). An
// explicit includeReview override surfaces review people when a workflow requires it.
export function getOperationalRoster(members = [], bookings = [], seasonId, { includeReview = false, currentSeasonId = CURRENT_SEASON_ID } = {}) {
  const base = getMembersForSeason(members, bookings, seasonId);
  if (seasonId !== currentSeasonId || includeReview) return base;
  return base.filter((m) => resolveCurrentTeamState(m, seasonId) === TEAM_STATE.CURRENT);
}

// The roster-review people for the current season (confirmed-current excluded) — for an
// explicit review/override surface only, never the default operating team.
export function getReviewRoster(members = [], bookings = [], seasonId, { currentSeasonId = CURRENT_SEASON_ID } = {}) {
  if (seasonId !== currentSeasonId) return [];
  return getMembersForSeason(members, bookings, seasonId)
    .filter((m) => resolveCurrentTeamState(m, seasonId) === TEAM_STATE.REVIEW);
}

// §14 the default operational date for a season (no hard-coded dates). Historical → the
// season's first real date. Current → today (JST) if it falls inside the season's activity,
// else the next scheduled day on/after today, else the season's first scheduled day.
export function defaultOperationalDate(bookings = [], seasonId, range = null, { currentSeasonId = CURRENT_SEASON_ID, timeZone = LEGACY_OPS_TIMEZONE } = {}) {
  if (seasonId !== currentSeasonId) return range ? range.start : null;
  const todayKey = zonedDateKey(new Date(), timeZone);
  if (range) {
    const startKey = zonedDateKey(range.start, timeZone);
    const endKey = zonedDateKey(range.end, timeZone);
    if (todayKey >= startKey && todayKey <= endKey) return wallClockToInstant(todayKey, '12:00', timeZone);
  }
  const next = nextScheduledDayInSeason(bookings, seasonId, todayKey); // on/after today, else first
  if (next) return wallClockToInstant(next, '12:00', timeZone);
  return range ? range.start : wallClockToInstant(todayKey, '12:00', timeZone);
}

// §6 the ONE season derivation. Given the realtime source + a selected season, produce every
// season-scoped slice the workspace needs. selectedSeasonMembers ALWAYS comes from the
// canonical getMembersForSeason (current → confirmed-current + review; historical → that
// season's actual staff) — never "all current members".
export function deriveWorkspaceData({ bookings = [], members = [], seasonId, date = null, includeReview = false, currentSeasonId = CURRENT_SEASON_ID, timeZone = LEGACY_OPS_TIMEZONE } = {}) {
  const seasonBookings = scopeToSeason(bookings, seasonId);
  const seasonMembers = getOperationalRoster(members, bookings, seasonId, { includeReview, currentSeasonId });
  const metrics = seasonMetrics(bookings, members, seasonId);
  const range = seasonDateRange(bookings, seasonId);
  const unassigned = buildUnassignedQueue(seasonBookings);
  return {
    seasonId,
    seasonLabel: seasonLabel(seasonId),
    // Pass 82C.2 — read-only is relative to the ORG's current season; the selected default date is in the ORG's zone.
    readOnly: isReadOnlySeason(seasonId, currentSeasonId),
    seasonBookings,
    seasonMembers,
    metrics,
    dateRange: range,
    unassigned,
    selectedDate: date || defaultOperationalDate(bookings, seasonId, range, { currentSeasonId, timeZone }),
  };
}

// §1 season+date is ONE context: a date outside the selected season's range snaps to the
// nearest valid in-season boundary (never "2024/25 roster + Dec 2026 date"). Pure/testable.
export function clampDateToSeason(date, range, { timeZone = LEGACY_OPS_TIMEZONE } = {}) {
  if (!date || !range || !range.start || !range.end) return date;
  const k = zonedDateKey(date, timeZone), s = zonedDateKey(range.start, timeZone), e = zonedDateKey(range.end, timeZone);
  if (k < s) return range.start;
  if (k > e) return range.end;
  return date;
}

// §2 the deprecated `schedule` route deep-links into Bookings → Day. Pure so the shell's
// redirect behaviour is unit-testable.
export function resolveBookingsEntry(tabFromRoute) {
  // §2 deprecated schedule route → Bookings → Day.
  if (tabFromRoute === 'schedule') return { tab: 'bookings', view: 'day', redirected: true };
  // Pass 58G — Bookings leads with the SCHEDULE (Calendar), not audit machinery or the sales queue.
  return { tab: tabFromRoute, view: 'calendar', redirected: false };
}

// Pass 82C.2 — Monday-00:00 in the ORGANIZATION's timezone (not the browser's week boundary). Defaults to the legacy
// SAS zone so SAS callers stay on the Asia/Tokyo week boundary.
export function weekStartOf(date, timeZone = LEGACY_OPS_TIMEZONE) {
  return zonedWeekStart(date || new Date(), timeZone);
}

// §13 the weekly staff schedule: staff rows × 7 days, each cell a list of that person's timed
// activities. Rows are the SELECTED-SEASON operational staff (drivers included), not just
// teaching instructors. Same derived data — no independent booking query/model.
export function buildWeekSchedule({ bookings = [], members = [], weekStart, conflicts = new Set(), timeZone = LEGACY_OPS_TIMEZONE } = {}) {
  const start = weekStartOf(weekStart, timeZone);
  const days = Array.from({ length: 7 }, (_, i) => dateKeyOf(new Date(start.getTime() + i * DAY_MS), timeZone));
  const daySet = new Set(days);
  const byStaffDay = {};
  for (const b of bookings) {
    if (!b.instructorId || b.status === 'canceled') continue;
    // Pass 59B — a COVERED schedule-grid hold is already represented by its real client booking; do not
    // render it separately in the week grid (no double occupancy). Standalone holds DO render.
    if (isCoveredGridHold(b)) continue;
    // Pass 58A: date-only staff activities (RDO/timeoff) carry no startAt — bucket them by their
    // canonical serviceDate so they appear in the Week grid too (Day already falls back this way).
    const k = b.startAt ? dateKeyOf(b.startAt, timeZone) : String(b.serviceDate || '');
    if (!daySet.has(k)) continue;
    const bucket = (byStaffDay[b.instructorId] = byStaffDay[b.instructorId] || {});
    (bucket[k] = bucket[k] || []).push(b);
  }
  const rows = members.map((m) => {
    const byDay = byStaffDay[m.userId] || {};
    let entryCount = 0; let hasConflict = false;
    for (const k of days) {
      const list = (byDay[k] || []).sort((a, b) => (msOf(a.startAt) || 0) - (msOf(b.startAt) || 0));
      byDay[k] = list;
      entryCount += list.length;
      if (list.some((b) => conflicts.has(b.id))) hasConflict = true;
    }
    return { staffId: m.userId, staffName: m.name || m.displayName || m.userId, member: m, byDay, entryCount, hasConflict };
  });
  // staff with entries first, then alphabetical — keeps the grid useful
  rows.sort((a, b) => (b.entryCount > 0) - (a.entryCount > 0) || a.staffName.localeCompare(b.staffName));
  return { weekStart: start, days, rows };
}

export { deriveActivityType, ACTIVITY, ACTIVITY_LABEL, deriveSeasonId };
